import { getStoreCodeFromAtlasLocale, useLocaleForAtlas } from '@portal/shared/utils/offer/generic';
import { useCallback } from 'react';
import { isDoubleSubscription } from '@portal/shared/utils/check/isDoubleSubscription';
import { getPrice } from '@portal/shared/utils/customer';
import { useStoreCurrency } from '@portal/state/hooks/useStoreCurrency';
import { usePageContext } from '@portal/processes/Page/PageContext';
import { extractNumberFromCurrencyString } from '@portal/shared/studio/AddToCartSectionShop/utils';
import { getFormattedShopifyId } from '@portal/shared/utils/subscriptionV2Helpers';
import {
  OFFER_PLAN,
  OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE,
  OFFER_SINGLE_OPTION_DISCOUNT_TYPE
} from '@ag1/atlas';
import { CustomOfferPricesItem, FREE_SHOPIFY_PRODUCT_IDENTIFIERS } from '../../shared';
import { getRawOfferDiscountedPrice } from './utils';
import { EmptyPricingConfig, PricingConfig } from '../types';

const { Discount } = OFFER_PLAN;
const { WholeOrder } = OFFER_SINGLE_OPTION_DISCOUNT_TYPE;
const { Percentage } = OFFER_SINGLE_OPTION_DISCOUNT_ENTRY_TYPE;

export const useOfferPriceHook = () => {
  const atlasLocale = useLocaleForAtlas();
  const store = getStoreCodeFromAtlasLocale(atlasLocale);

  const { symbolCurrency } = useStoreCurrency();
  const { offer, coreProducts, shopifyVariantsByStoreCode } = usePageContext();

  const getShopifyProductPricingBreakdowns = useCallback(
    (priceConfigRow: CustomOfferPricesItem, { isCoreProduct }: { isCoreProduct: boolean }) => {
      // Ensure that this is a number so we're not accidentally adding strings.
      let rawPrice = parseFloat(`${priceConfigRow.price}`);

      if (Number.isNaN(rawPrice)) {
        rawPrice = 0;
      }

      if (isCoreProduct) {
        const isDoubleSub = isDoubleSubscription(store, priceConfigRow.shopifyVariantId || '');

        let price = getPrice(symbolCurrency, rawPrice);
        let rawOriginalPrice = isDoubleSub ? rawPrice + 49 : rawPrice + 20;
        let originalPrice = getPrice(symbolCurrency, rawOriginalPrice);

        const firstOfferSubscription = (offer?.subscriptions || [])?.[0];

        const shopifyVariantId = getFormattedShopifyId(
          'ProductVariant',
          priceConfigRow.shopifyVariantId || ''
        );

        if (shopifyVariantId) {
          const shopifyVariantData = shopifyVariantsByStoreCode?.[store]?.find(
            (i) => i.id === shopifyVariantId
          );
          if (shopifyVariantData) {
            const coreProduct = coreProducts?.find(
              (i) => i.id === firstOfferSubscription?.offer_subscription_type_id
            );
            const quantity = coreProduct?.quantity || priceConfigRow.quantity;
            const shopifyPrice = quantity
              ? Number(
                  shopifyVariantData?.contextualPricing?.price?.amount || shopifyVariantData.price
                ) * quantity
              : Number(
                  shopifyVariantData?.contextualPricing?.price?.amount || shopifyVariantData.price
                );

            if (!Number.isNaN(shopifyPrice)) {
              rawPrice = shopifyPrice;
              price = getPrice(symbolCurrency, shopifyPrice);

              if (shopifyVariantData.product.originalPrice) {
                const baseOriginalPrice =
                  extractNumberFromCurrencyString(shopifyVariantData.product.originalPrice.value) ??
                  0;
                rawOriginalPrice = baseOriginalPrice * (quantity ?? 1);
                originalPrice = getPrice(symbolCurrency, rawOriginalPrice);
              } else {
                originalPrice = getPrice(symbolCurrency, shopifyPrice + (isDoubleSub ? 49 : 20));
              }
            }
          }
        }

        return { price, rawPrice, originalPrice, rawOriginalPrice };
      }

      // addon pricing calculations
      const rawOriginalPrice = rawPrice;
      const originalPrice = getPrice(symbolCurrency, rawOriginalPrice);
      const shopifyVariantId = getFormattedShopifyId(
        'ProductVariant',
        priceConfigRow.shopifyVariantId || ''
      );
      const shopifyVariantData = shopifyVariantsByStoreCode?.[store]?.find(
        (i) => i.id === shopifyVariantId
      );

      if (
        shopifyVariantData &&
        shopifyVariantData.product.tags.some((x) => FREE_SHOPIFY_PRODUCT_IDENTIFIERS.includes(x))
      ) {
        rawPrice = 0;
      }

      const price = getPrice(symbolCurrency, rawPrice);

      return { price, rawPrice, originalPrice, rawOriginalPrice };
    },
    [offer?.subscriptions, shopifyVariantsByStoreCode, coreProducts, symbolCurrency, store]
  );

  const getMultipleOptionsOfferPricingConfig = useCallback(
    (priceRowsData: CustomOfferPricesItem[]): PricingConfig | EmptyPricingConfig => {
      const coreProductConfig = priceRowsData.length ? priceRowsData[0] : null;

      let addonProductsConfig = priceRowsData.slice(1).map((config) => {
        const pricingBreakdown = getShopifyProductPricingBreakdowns(config, {
          isCoreProduct: false
        });
        return { ...config, ...pricingBreakdown };
      });

      if (coreProductConfig) {
        let total = 0;
        let discountedTotal = 0;

        const { rawPrice, originalPrice, rawOriginalPrice } = getShopifyProductPricingBreakdowns(
          coreProductConfig,
          { isCoreProduct: true }
        );

        total += rawOriginalPrice;
        discountedTotal += rawPrice;

        addonProductsConfig = addonProductsConfig.map((addon) => {
          let rawAddonPrice = addon.rawPrice;

          if (addon.discountType && addon.discountAmount) {
            rawAddonPrice = getRawOfferDiscountedPrice(rawAddonPrice, {
              type: addon.discountType,
              amount: addon.discountAmount
            });
          }

          total += addon.rawOriginalPrice;
          discountedTotal += rawAddonPrice;

          return {
            ...addon,
            rawPrice: rawAddonPrice,
            price: getPrice(symbolCurrency, rawAddonPrice)
          };
        });

        return {
          coreProduct: {
            ...coreProductConfig,
            // This is the displayed line item price for the main product (when discounted for multiple and single non-discount offer types).
            price: getPrice(symbolCurrency, rawPrice),
            // Used for comparisons or internal calculations (e.g., detecting if total === 0)
            rawPrice,
            // Used to render a strikethrough for original price (when discounted for multiple and single non-discount offer types).
            originalPrice,
            rawOriginalPrice
          },
          addonProducts: addonProductsConfig,
          // The formatted full price before the discount.
          total: getPrice(symbolCurrency, total),
          rawTotal: total,
          // The formatted final price the customer pays.
          discountedTotal: getPrice(symbolCurrency, discountedTotal),
          // Used for comparisons or internal calculations (e.g., detecting if total === 0)
          rawDiscountedTotal: discountedTotal
        };
      }

      return {
        coreProduct: null,
        addonProducts: null,
        total: null,
        rawTotal: null,
        discountedTotal: null,
        rawDiscountedTotal: null
      };
    },
    [getShopifyProductPricingBreakdowns, symbolCurrency]
  );

  const getSingleOptionOfferPricingConfig = useCallback(
    (priceRowsData: CustomOfferPricesItem[]): PricingConfig | EmptyPricingConfig => {
      const coreProductsConfig = priceRowsData.filter((x) => x.intendedUsage === 'subscription');
      const addonProductsConfig = priceRowsData.filter((y) => y.intendedUsage === 'addon');

      const hasDiscounts = offer?.type === Discount;

      let total = 0;
      let discountedTotal = 0;

      if (coreProductsConfig.length || addonProductsConfig.length) {
        const updatedCoreProductsConfig = coreProductsConfig.map((core) => {
          const { rawPrice, originalPrice, rawOriginalPrice } = getShopifyProductPricingBreakdowns(
            core,
            { isCoreProduct: true }
          );

          let rawOfferPrice = rawPrice;

          if (core.discountType && core.discountAmount) {
            rawOfferPrice = getRawOfferDiscountedPrice(rawOfferPrice, {
              type: core.discountType,
              amount: core.discountAmount
            });
          }

          const offerPrice = getPrice(symbolCurrency, rawOfferPrice);

          total += rawOriginalPrice;
          discountedTotal += rawOfferPrice;

          return {
            ...core,
            // This is the displayed line item price for the main product (when discounted for multiple and single non-discount offer types).
            price: offerPrice,
            // Used for comparisons or internal calculations (e.g., detecting if total === 0)
            rawPrice: rawOfferPrice,
            // Used to render a strikethrough for original price (when discounted for multiple and single non-discount offer types).
            originalPrice,
            rawOriginalPrice
          };
        });

        const updatedAddonProductsConfig = addonProductsConfig.map((addon) => {
          const { rawPrice, originalPrice, rawOriginalPrice } = getShopifyProductPricingBreakdowns(
            addon,
            { isCoreProduct: false }
          );
          let rawOfferPrice = rawPrice;

          if (hasDiscounts && addon.discountType && addon.discountAmount && rawOfferPrice > 0) {
            rawOfferPrice = getRawOfferDiscountedPrice(rawOfferPrice, {
              type: addon.discountType,
              amount: addon.discountAmount
            });
          }

          const offerPrice = getPrice(symbolCurrency, rawOfferPrice);

          total += rawOriginalPrice;
          discountedTotal += rawOfferPrice;

          return {
            ...addon,
            price: offerPrice,
            rawPrice: rawOfferPrice,
            originalPrice,
            rawOriginalPrice
          };
        });

        if (hasDiscounts && offer?.single_option_discount_type[atlasLocale] === WholeOrder) {
          const discountConfig = offer?.single_option_discount_config[atlasLocale][0] || {
            type: Percentage,
            value: 0
          };
          discountedTotal = getRawOfferDiscountedPrice(discountedTotal, {
            type: discountConfig.type,
            amount: discountConfig.amount
          });
        }

        return {
          coreProducts: updatedCoreProductsConfig,
          addonProducts: updatedAddonProductsConfig,
          // The formatted full price before the discount.
          total: getPrice(symbolCurrency, total),
          rawTotal: total,
          // The formatted final price the customer pays.
          discountedTotal: getPrice(symbolCurrency, discountedTotal),
          // Used for comparisons or internal calculations (e.g., detecting if total === 0)
          rawDiscountedTotal: discountedTotal
        };
      }

      return {
        coreProduct: null,
        addonProducts: null,
        total: null,
        rawTotal: null,
        discountedTotal: null,
        rawDiscountedTotal: null
      };
    },
    [getShopifyProductPricingBreakdowns, symbolCurrency, offer, atlasLocale]
  );

  return {
    getShopifyProductPricingBreakdowns,
    getMultipleOptionsOfferPricingConfig,
    getSingleOptionOfferPricingConfig
  };
};
